import React, { useEffect, useRef } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import {
  sanitizeQuillHtml,
  stripOuterP,
  wrapWithOuterP,
} from "../utils/quillHtml";

const EMPTY_QUILL_HTML = "<p><br></p>";

// Quill editor bound to a single field inside a FormGroup array item.
// Use inside QuestionsAnswersArray when textBox[2] === "richtext".
const QuillArrayFieldEditor = ({ itemControl, fieldName }) => {
  const containerRef = useRef(null);
  const quillRef = useRef(null);
  const isExternalUpdate = useRef(false);

  useEffect(() => {
    if (!containerRef.current || quillRef.current) return;

    const quill = new Quill(containerRef.current, {
      theme: "snow",
      modules: {
        toolbar: [[{ script: "sub" }, { script: "super" }]],
      },
    });
    quillRef.current = quill;

    // Backend stores HTML without the outer <p> wrapper, so re-wrap for Quill.
    const initialValue = itemControl.get(fieldName)?.value || "";
    if (initialValue) {
      isExternalUpdate.current = true;
      quill.clipboard.dangerouslyPasteHTML(wrapWithOuterP(initialValue));
      isExternalUpdate.current = false;
    }

    quill.on("text-change", () => {
      if (isExternalUpdate.current) return;
      const html = quill.root.innerHTML;
      // Strip everything but <sup>/<sub> (and their attributes) and decode
      // HTML entities so the value submitted to the backend stays clean even
      // when the user pastes rich content from Word.
      itemControl.patchValue({
        [fieldName]:
          html === EMPTY_QUILL_HTML ? "" : sanitizeQuillHtml(stripOuterP(html)),
      });
    });

    return () => {
      quill.off("text-change");
    };
  }, []); // intentionally empty — Quill is initialized once on mount

  useEffect(() => {
    const control = itemControl.get(fieldName);
    if (!control) return;

    const subscription = control.valueChanges.subscribe(value => {
      const quill = quillRef.current;
      if (!quill) return;

      const currentHtml = quill.root.innerHTML;
      // Normalize both sides to the sanitized backend (unwrapped) format for
      // comparison, then re-wrap before pasting so Quill receives a valid <p>
      // block. Sanitizing the current side too keeps typing from triggering a
      // re-paste (which would reset the cursor) when the editor still holds
      // disallowed markup that the stored value has already dropped.
      const normalizedCurrent =
        currentHtml === EMPTY_QUILL_HTML
          ? ""
          : sanitizeQuillHtml(stripOuterP(currentHtml));
      const newValue = value || "";

      if (normalizedCurrent !== newValue) {
        isExternalUpdate.current = true;
        quill.clipboard.dangerouslyPasteHTML(wrapWithOuterP(newValue));
        isExternalUpdate.current = false;
      }
    });

    return () => {
      if (subscription && typeof subscription.unsubscribe === "function") {
        subscription.unsubscribe();
      }
    };
  }, []); // intentionally empty — subscribe once, props are stable per mount

  return (
    <div className="quill-editor-wrapper">
      <div ref={containerRef} />
    </div>
  );
};

export default QuillArrayFieldEditor;
